import { Component } from '@theme/component';
import {
  ProductArVariantUpdateEvent,
  SlideshowSelectEvent,
  ThemeEvents,
  ZoomMediaSelectedEvent,
} from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });

    const initialVariantMediaTemplate = this.querySelector(
      'template[data-variant-primary-media][data-apply-initial-variant-media]'
    );
    const initialVariantMedia =
      initialVariantMediaTemplate instanceof HTMLTemplateElement
        ? initialVariantMediaTemplate.content.querySelector('.product-media')
        : null;

    if (initialVariantMedia) {
      void this.#updatePrimaryMedia(initialVariantMedia, { clearThumbnailSelection: true });
    }

    this.#loadShopifyXR();
  }

  #controller = new AbortController();

  #primaryMediaUpdateId = 0;

  #variantResponseUpdateId = 0;

  /** @type {HTMLElement | null} */
  #originalPrimaryMedia = null;

  #variantPrimaryMediaActive = false;

  /**
   * Loads Shopify XR when the gallery is first rendered and whenever variant
   * selection replaces the gallery with new model data.
   */
  #loadShopifyXR() {
    const modelJSON = this.querySelector('[id^="ProductModelJSON-"]');
    if (!modelJSON || !window.Shopify?.loadFeatures) return;

    Shopify.loadFeatures([
      {
        name: 'shopify-xr',
        version: '1.0',
        onLoad: (errors) => this.#setupShopifyXR(errors),
      },
    ]);
  }

  /**
   * Registers the current variant's models and activates its AR buttons.
   * @param {Error[] | undefined} errors
   */
  #setupShopifyXR(errors) {
    if (errors) return;

    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', () => this.#setupShopifyXR(), {
        once: true,
        signal: this.#controller.signal,
      });
      return;
    }

    const modelJSON = /** @type {HTMLScriptElement | null} */ (
      this.querySelector('[id^="ProductModelJSON-"]')
    );
    if (!modelJSON || modelJSON.dataset.shopifyXrLoaded === 'true') return;

    try {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent || '[]'));
      modelJSON.dataset.shopifyXrLoaded = 'true';
      window.ShopifyXR.setupXRElements();
    } catch (error) {
      console.warn('[media-gallery] Shopify XR initialization failed:', error);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const updateId = ++this.#variantResponseUpdateId;

    event.promise
      .then(({ detail }) => {
        if (updateId !== this.#variantResponseUpdateId || !detail?.html) return;

        const { html } = detail;
        document.dispatchEvent(new ProductArVariantUpdateEvent(html));

        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // On opted-in custom product templates, a variant changes only the large
        // gallery image. Keep the product gallery and its thumbnails as-is.
        const variantPrimaryMediaTemplate = newMediaGallery.querySelector('template[data-variant-primary-media]');
        if (variantPrimaryMediaTemplate instanceof HTMLTemplateElement) {
          const newPrimaryMedia = variantPrimaryMediaTemplate.content.querySelector('.product-media');

          if (newPrimaryMedia) {
            void this.#updatePrimaryMedia(newPrimaryMedia, {
              clearThumbnailSelection: variantPrimaryMediaTemplate.hasAttribute('data-clear-thumbnail-selection'),
            });
          }

          return;
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Preloads and decodes a variant image, then updates the existing large image in place.
   * Keeping the current DOM node visible prevents the gallery from flashing
   * while the browser downloads and decodes the new source.
   * @param {Element} newPrimaryMedia
   * @param {{ clearThumbnailSelection?: boolean }} [options]
   */
  async #updatePrimaryMedia(newPrimaryMedia, { clearThumbnailSelection = false } = {}) {
    const updateId = ++this.#primaryMediaUpdateId;

    if (this.#variantPrimaryMediaActive) this.#restoreOriginalPrimaryMedia();

    const currentPrimaryMedia = this.querySelector('slideshow-slide .product-media');
    const currentImage = currentPrimaryMedia?.querySelector('.product-media__image');
    const newImage = newPrimaryMedia.querySelector('.product-media__image');

    if (!(currentPrimaryMedia instanceof HTMLElement) || !(currentImage instanceof HTMLImageElement)) return;
    if (!(newImage instanceof HTMLImageElement)) return;

    if (clearThumbnailSelection && !this.#originalPrimaryMedia) {
      this.#originalPrimaryMedia = /** @type {HTMLElement} */ (currentPrimaryMedia.cloneNode(true));
    }

    const source = newImage.getAttribute('src');
    if (!source) return;

    const preloadedImage = new Image();
    const sizes = newImage.getAttribute('sizes');
    const srcset = newImage.getAttribute('srcset');

    try {
      await new Promise((resolve, reject) => {
        preloadedImage.onload = resolve;
        preloadedImage.onerror = reject;

        if (sizes) preloadedImage.sizes = sizes;
        if (srcset) preloadedImage.srcset = srcset;
        preloadedImage.src = source;
      });
      await preloadedImage.decode();
    } catch (error) {
      console.warn('[media-gallery] Variant image preload failed:', error);
      return;
    }

    // Ignore an older image if another variant was selected while it loaded.
    if (updateId !== this.#primaryMediaUpdateId || !this.isConnected) return;

    for (const attribute of Array.from(currentImage.attributes)) {
      if (!newImage.hasAttribute(attribute.name)) currentImage.removeAttribute(attribute.name);
    }

    for (const attribute of Array.from(newImage.attributes)) {
      currentImage.setAttribute(attribute.name, attribute.value);
    }

    currentPrimaryMedia.setAttribute('style', newPrimaryMedia.getAttribute('style') || '');
    currentPrimaryMedia.setAttribute('data-media-id', newPrimaryMedia.getAttribute('data-media-id') || '');
    await this.slideshow?.select(0, undefined, { animate: false });

    if (clearThumbnailSelection) {
      this.#variantPrimaryMediaActive = true;

      if (this.slideshow) {
        // Treat variant media as a temporary slide immediately before the
        // product gallery, so the first navigation reveals product media #1.
        this.slideshow.current = -1;
      } else {
        for (const thumbnail of this.querySelectorAll('.slideshow-controls__thumbnail[aria-selected]')) {
          thumbnail.setAttribute('aria-selected', 'false');
        }
      }
    }
  }

  /** Restores product media #1 before navigating the regular gallery. */
  #restoreOriginalPrimaryMedia() {
    const currentPrimaryMedia = this.querySelector('slideshow-slide .product-media');
    const restoredPrimaryMedia = this.#originalPrimaryMedia?.cloneNode(true);

    if (currentPrimaryMedia instanceof HTMLElement && restoredPrimaryMedia instanceof HTMLElement) {
      currentPrimaryMedia.replaceWith(restoredPrimaryMedia);
    }

    this.#variantPrimaryMediaActive = false;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Keeps the shortcut state aligned with thumbnail, arrow, swipe, and shortcut navigation.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (this.#variantPrimaryMediaActive) this.#restoreOriginalPrimaryMedia();

    const selectedSlide = event.detail.slide;
    let selectedKind = 'image';

    if (event.detail.index === this.#dimensionsMediaIndex) {
      selectedKind = 'dimensions';
    } else if (selectedSlide.classList.contains('product-media-container--model')) {
      selectedKind = 'model';
    }

    for (const shortcut of this.#mediaShortcutButtons) {
      shortcut.setAttribute('aria-pressed', `${shortcut.dataset.mediaShortcutKind === selectedKind}`);
    }
  };

  /**
   * Selects the requested main product media from a shortcut button.
   * @param {number|string} index
   * @param {MouseEvent} event
   */
  selectMediaShortcut(index, event) {
    const requestedIndex = Number(index);
    if (!Number.isInteger(requestedIndex)) return;

    this.slideshow?.select(requestedIndex, event);
  }

  get #mediaShortcutButtons() {
    return /** @type {NodeListOf<HTMLButtonElement>} */ (this.querySelectorAll('.media-gallery__shortcut'));
  }

  get #dimensionsMediaIndex() {
    const dimensionsShortcut = Array.from(this.#mediaShortcutButtons).find(
      (shortcut) => shortcut.dataset.mediaShortcutKind === 'dimensions'
    );

    return Number(dimensionsShortcut?.dataset.mediaShortcutIndex ?? -1);
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
